/* ===================================================================
   State bootstrap — turn the /bootstrap response (or null when the
   API is unreachable) into the InitialState the AppInner expects. On
   first boot we seed the library locally and write it back through
   the relational API so the server starts in a useful state.
=================================================================== */
import { saveCollection, saveUiState, type BootstrapResponse } from "./api/client";
import {
  SEED_CORPUSES,
  SEED_EVALSETS,
  SEED_WS_BUNDLES,
  SEED_WORKSPACES,
} from "./seeds";
import type { InitialState, RunStateSnapshot } from "./types";

/* Agent bundles are NOT part of the bootstrap state anymore: the workbench
   and the simulation read them from the agent-bundles API (the tables are
   the source of truth; the server seeds a canonical bundle on boot). The
   bootstrap's checkVersions blob remains server-side purely as a migration
   input. */

export function buildInitial(s: BootstrapResponse | null | undefined): InitialState {
  const reachable = !!s;
  const seeded = !s || !s.initialized;
  /* An initialized server with zero matters (all corpora deleted / never
     synced) must still boot: AppShell indexes corpuses[0] unconditionally,
     so an empty list bricks the app on a blank screen. Fall back to seeds. */
  const storedCorpuses = (!seeded && (s!.corpuses as InitialState["corpuses"])) || [];
  const initial: InitialState = {
    corpuses: seeded || !storedCorpuses.length ? SEED_CORPUSES : storedCorpuses,
    evalSets: (seeded ? SEED_EVALSETS : (s!.evalSets as InitialState["evalSets"])) || [],
    wsBundles: (seeded ? SEED_WS_BUNDLES : (s!.wsBundles as InitialState["wsBundles"])) || [],
    workspaces: (seeded ? SEED_WORKSPACES : (s!.workspaces as InitialState["workspaces"])) || [],
    ui: (!seeded && (s!.ui as InitialState["ui"])) || null,
    runStates: (s?.runStates as Record<string, RunStateSnapshot>) || {},
  };
  if (reachable && seeded) {
    saveCollection("eval-sets", initial.evalSets);
    saveCollection("ws-bundles", initial.wsBundles);
    saveCollection("workspaces", initial.workspaces);
    saveUiState("meta", { initialized: true, seededAt: new Date().toISOString() });
    // Seed corpuses persist via the matter-sync reconcile pass on mount.
  }
  return initial;
}
